import TimelineIcon from "@mui/icons-material/Timeline";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import ContractorReferralPortalApi from "../../../../infrastructure/api/contractorReferralPortal";
import RequestWorkflowTracker from "../../../components/request/tracking/RequestWorkflowTracker";
import {
  buildCustomerTrackingModel,
  formatRequestCode,
} from "../../../components/request/tracking/requestTrackingModel";
import { TRACKING_PAGE_BG } from "../../../components/request/tracking/trackingStyles";

const pick = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("fa-IR") : "—";

const getStepStatusColor = (status) => {
  const value = String(status ?? "").toLowerCase();
  if (value.includes("complet") || value === "3") return "success";
  if (value.includes("process") || value === "2") return "primary";
  if (value.includes("reject") || value.includes("cancel") || value === "4") return "error";
  return "default";
};

const ContractorReferralStagesModal = ({ open, attributionId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tracking, setTracking] = useState(null);

  useEffect(() => {
    if (!open || !attributionId) {
      setTracking(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await ContractorReferralPortalApi.getAttributionTracking(attributionId);
        const { error: err, data } = ContractorReferralPortalApi.normalizeTracking(res);
        if (cancelled) return;
        if (err) {
          setError(err);
          setTracking(null);
          return;
        }
        setTracking(data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "خطا در بارگذاری تاریخچه");
          setTracking(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, attributionId]);

  const referralStages = useMemo(() => {
    const items = tracking?.referralStages ?? tracking?.ReferralStages ?? [];
    return Array.isArray(items) ? items : [];
  }, [tracking]);

  const stepHistory = useMemo(() => {
    const items = tracking?.stepHistory ?? tracking?.StepHistory ?? [];
    return Array.isArray(items) ? items : [];
  }, [tracking]);

  const model = useMemo(() => {
    if (!tracking) return null;
    const milestones =
      tracking.trackingMilestones || tracking.TrackingMilestones || [];
    if (!Array.isArray(milestones) || milestones.length === 0) return null;

    return buildCustomerTrackingModel({
      request: {
        referenceID: pick(tracking, "referenceId", "ReferenceId"),
        requestConfirmTitle: pick(tracking, "statusTitle", "StatusTitle"),
        requestConfirm: "InProcess",
      },
      trackingMilestones: milestones,
      parallelTrackingMilestones:
        tracking.parallelTrackingMilestones || tracking.ParallelTrackingMilestones || [],
      activeTrackingMilestoneIndex:
        tracking.activeTrackingMilestoneIndex ?? tracking.ActiveTrackingMilestoneIndex ?? 0,
      parallelInsertAfterMainIndex:
        tracking.parallelInsertAfterMainIndex ?? tracking.ParallelInsertAfterMainIndex ?? 1,
      activeStepIndex: 0,
      shownStepIndex: 0,
    });
  }, [tracking]);

  const currentTitle =
    pick(tracking, "currentStageTitle", "CurrentStageTitle") ||
    model?.currentStageTitle ||
    pick(tracking, "statusTitle", "StatusTitle") ||
    "—";
  const currentDescription =
    pick(tracking, "currentStageDescription", "CurrentStageDescription") ||
    model?.currentStageDescription;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TimelineIcon color="primary" />
        تاریخچه مراحل معرفی
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !tracking ? (
          <Alert severity="info">اطلاعاتی برای نمایش وجود ندارد.</Alert>
        ) : (
          <Stack spacing={2.5}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color="primary"
                  label={pick(tracking, "statusTitle", "StatusTitle") || "—"}
                />
                {pick(tracking, "registeredUserMobile", "RegisteredUserMobile") ? (
                  <Typography variant="body2">
                    موبایل: {pick(tracking, "registeredUserMobile", "RegisteredUserMobile")}
                  </Typography>
                ) : null}
                {pick(tracking, "referenceId", "ReferenceId") ? (
                  <Typography variant="body2">
                    پیگیری: {formatRequestCode(pick(tracking, "referenceId", "ReferenceId"))}
                  </Typography>
                ) : null}
                {pick(tracking, "projectCode", "ProjectCode") ? (
                  <Typography variant="body2">
                    کد پروژه: {pick(tracking, "projectCode", "ProjectCode")}
                  </Typography>
                ) : null}
              </Stack>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1.5 }}>
                {currentTitle}
              </Typography>
              {currentDescription ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {currentDescription}
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                مسیر معرفی
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>مرحله</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>زمان</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralStages.map((stage) => {
                    const completed = stage.isCompleted ?? stage.IsCompleted;
                    const current = stage.isCurrent ?? stage.IsCurrent;
                    return (
                      <TableRow key={stage.code ?? stage.Code}>
                        <TableCell>{stage.title ?? stage.Title}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={current ? "primary" : completed ? "success" : "default"}
                            label={current ? "جاری" : completed ? "انجام‌شده" : "در انتظار"}
                            variant={current ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(stage.occurredAt ?? stage.OccurredAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {model ? (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    مراحل پیشرفت پروژه
                  </Typography>
                  <Box sx={{ background: TRACKING_PAGE_BG, borderRadius: 2, p: 1.5 }}>
                    <RequestWorkflowTracker
                      timelineSteps={model.timelineSteps}
                      parallelTimelineSteps={model.parallelTimelineSteps}
                      parallelInsertAfterMainIndex={model.parallelInsertAfterMainIndex}
                      activeIndex={model.activeTimelineIndex}
                      currentStageTitle={currentTitle}
                      currentStageDescription={currentDescription}
                    />
                  </Box>
                </Box>
              </>
            ) : null}

            {stepHistory.length > 0 ? (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    تاریخچه مراحل پروژه
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>مرحله</TableCell>
                        <TableCell>وضعیت</TableCell>
                        <TableCell>شروع</TableCell>
                        <TableCell>پایان</TableCell>
                        <TableCell>روز سپری‌شده</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stepHistory.map((step, index) => (
                        <TableRow
                          key={`${pick(step, "stepInfoId", "StepInfoId") || index}-${index}`}
                        >
                          <TableCell>{pick(step, "stepTitle", "StepTitle") || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={getStepStatusColor(
                                pick(step, "stepStatus", "StepStatus")
                              )}
                              label={pick(step, "stepStatusTitle", "StepStatusTitle") || "—"}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(pick(step, "startDateTime", "StartDateTime"))}
                          </TableCell>
                          <TableCell>
                            {formatDate(pick(step, "endDateTime", "EndDateTime"))}
                          </TableCell>
                          <TableCell>
                            {pick(step, "daysSpent", "DaysSpent") != null
                              ? `${pick(step, "daysSpent", "DaysSpent")} روز`
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractorReferralStagesModal;
